import { useCallback } from 'react';
import { toast } from 'sonner';

import { TableColumn } from '@/components/common/DataTable';
import { PaginationState } from '@/hooks/usePagination';
import { toQueryParams } from '@/utils/pagination';

/** Config for entity-specific CSV export endpoint */
export interface DataTableExportConfig {
  endpoint: string;
  entityName: string;
  csvKeyOverrides?: Record<string, string>;
}

export interface UseDataTableExportParams<T = unknown> {
  config: DataTableExportConfig;
  queryState: PaginationState;
  visibleColumns: string[];
  allColumns: TableColumn<T>[];
}

export interface UseDataTableExportReturn {
  handleExport: () => void;
  isExporting: boolean;
}

/**
 * Generic hook for CSV export.
 * Works with any entity (Tasks, Matters, Intakes).
 * Backend accepts frontend column IDs directly - no mapping needed.
 */
export const useDataTableExport = <T = unknown>({
  config,
  queryState,
  visibleColumns,
  allColumns,
}: UseDataTableExportParams<T>): UseDataTableExportReturn => {
  const handleExport = useCallback(() => {
    const params = new URLSearchParams();

    const queryParams = toQueryParams(queryState);

    Object.entries(queryParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach((v) => params.append(key, String(v)));
        } else {
          params.set(key, String(value));
        }
      }
    });

    // Map column IDs to backend CSV keys
    const csvColumns = visibleColumns
      .map((colId) => {
        if (config.csvKeyOverrides?.[colId])
          return config.csvKeyOverrides[colId];
        const col = allColumns.find((c) => c.id === colId);
        return col?.accessorKey || colId;
      })
      .filter(Boolean);

    params.set('columns', csvColumns.join(','));

    const url = `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}${config.endpoint}?${params.toString()}`;

    const toastId = toast.loading(`Preparing ${config.entityName} export...`, {
      description: 'This may take a few seconds',
    });

    // Fetch API is correct for file downloads (not RTK Query - downloads don't need caching)
    fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'text/csv' },
    })
      .then(async (response) => {
        if (!response.ok) {
          let errorMessage = `${config.entityName} export failed`;
          try {
            const errorData = await response.json();
            errorMessage =
              errorData.detail ||
              errorData.error ||
              `${response.statusText} (${response.status})`;
          } catch {
            errorMessage = `${response.statusText} (${response.status})`;
          }
          throw new Error(errorMessage);
        }

        const blob = await response.blob();
        const contentDisposition = response.headers.get('Content-Disposition');
        const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
        const filename =
          filenameMatch?.[1] || `${config.entityName}_export.csv`;

        // Trigger browser download
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);

        toast.success(`${config.entityName} export complete!`, {
          id: toastId,
          description: 'Check your downloads folder',
          duration: 3000,
        });
      })
      .catch((error) => {
        toast.error(`${config.entityName} export failed`, {
          id: toastId,
          description: error.message || 'Please try again or contact support',
          duration: 5000,
        });
      });
  }, [config, queryState, visibleColumns, allColumns]);

  return {
    handleExport,
    isExporting: false, // Could track with useState if progress indication needed
  };
};
